'use client';

// Extend the Window interface for $chatwoot
declare global {
  interface Window {
    $chatwoot?: { toggle: () => void };
  }
}

import Link from 'next/link';
import { useAuth } from '../../AuthContext';
import { useRouter } from 'next/navigation';
import { Menu, X, User, Phone, MessageSquare, ChevronDown } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';

export default function Header() {
  const { isAuthenticated, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [showAuthDropdown, setShowAuthDropdown] = useState(false);
  const [showSupportDropdown, setShowSupportDropdown] = useState(false);
  const router = useRouter();
  const authDropdownRef = useRef(null);
  const supportDropdownRef = useRef(null);


  useEffect(() => {
    const handleRouteChange = () => {
      setShowMenu(false);
      setShowAuthDropdown(false);
      setShowSupportDropdown(false);
    };
    window.addEventListener('popstate', handleRouteChange);
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      // Handle mobile menu
      if (showMenu) {
        const menu = document.getElementById('mobile-menu') as HTMLElement | null;
        const hamburgerBtn = document.getElementById('hamburger-button') as HTMLElement | null;
        if (
          menu &&
          !menu.contains(event.target as Node) &&
          hamburgerBtn &&
          !hamburgerBtn.contains(event.target as Node)
        ) {
          setShowMenu(false);
        }
      }

      // Handle auth dropdown
      if (
        showAuthDropdown &&
        authDropdownRef.current &&
        !(authDropdownRef.current as HTMLElement).contains(event.target as Node)
      ) {
        setShowAuthDropdown(false);
      }

      // Handle support dropdown
      if (
        showSupportDropdown &&
        supportDropdownRef.current &&
        !(supportDropdownRef.current as HTMLElement).contains(event.target as Node)
      ) {
        setShowSupportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMenu, showAuthDropdown, showSupportDropdown]);

  const handleLogout = async () => {
    try {
      setShowMenu(false);
      setShowAuthDropdown(false);
      await logout();
      router.push('/signin');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const handleLinkClick = () => {
    setShowMenu(false);
    setShowAuthDropdown(false);
    setShowSupportDropdown(false);
  };

  const handleChatClick = () => {
    // Trigger the woot chat widget
    if (typeof window !== 'undefined' && window.$chatwoot) {
      window.$chatwoot.toggle();
    } else {
      // Fallback if the widget isn't loaded yet
      const chatButton = document.querySelector('.woot-widget-bubble') as HTMLElement | null;
      if (chatButton) chatButton.click();
    }
    setShowSupportDropdown(false);
  };

  return (
    <header className="bg-[#0b1220] text-white sticky top-0 z-50 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link href="/" className="flex-shrink-0">
            <Image
              src="/images/MidaPos-POS-and-Pinpad-Systems-Free-No-Contract-Logo.webp"
              alt="MidaPOS Logo"
              width={168}
              height={40}
              className="w-[168px] h-[40px]"
              priority
            />
          </Link>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            <button
              id="hamburger-button"
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 rounded-md hover:bg-[#1a2536] transition-colors"
              aria-label={showMenu ? 'Close menu' : 'Open menu'}
            >
              {showMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            <Link href="/" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-[#1a2536] transition-colors">
              Home
            </Link>
            <Link href="/pricing" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-[#1a2536] transition-colors">
              Pricing
            </Link>
            <Link href="/products" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-[#1a2536] transition-colors">
              Products
            </Link>
            <Link href="/apply" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-[#1a2536] transition-colors">
              Apply
            </Link>
            <Link href="/get-started" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-[#1a2536] transition-colors">
              Get Started
            </Link>


            {/* Support Dropdown */}
            <div className="relative" ref={supportDropdownRef}>
              <button
                onClick={() => setShowSupportDropdown(!showSupportDropdown)}
                className="flex items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-[#1a2536] transition-colors"
              >
                <span className="mr-1">Support</span>
                <ChevronDown className="w-4 h-4 text-orange-500" />
              </button>

              {showSupportDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 ring-1 ring-black ring-opacity-5">
                  <a
                    href="tel:3053067774"
                    className="flex items-center px-4 py-3 text-sm text-gray-800 hover:bg-gray-100"
                    onClick={handleLinkClick}
                  >
                    <Phone className="w-4 h-4 mr-3 text-orange-500" />
                    <span>(305) 306-7774</span>
                  </a>
                  <button
                    onClick={handleChatClick}
                    className="flex items-center w-full text-left px-4 py-3 text-sm text-gray-800 hover:bg-gray-100"
                  >
                    <MessageSquare className="w-4 h-4 mr-3 text-orange-500" />
                    <span>Chat with us</span>
                  </button>
                  <a
                    href="mailto:support@midapos.com"
                    className="flex items-center px-4 py-3 text-sm text-gray-800 hover:bg-gray-100"
                    onClick={handleLinkClick}
                  >
                    <MessageSquare className="w-4 h-4 mr-3 text-orange-500" />
                    <span>support@midapos.com</span>
                  </a>
                </div>
              )}
            </div>

            {/* Auth Buttons/Dropdown */}
            <div className="relative" ref={authDropdownRef}>
              {isAuthenticated ? (
                <button
                  onClick={() => setShowAuthDropdown(!showAuthDropdown)}
                  className="flex items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-[#1a2536] transition-colors"
                >
                  <User className="w-5 h-5 mr-1" />
                  <ChevronDown className="w-4 h-4 text-orange-500" />
                </button>
              ) : (
                <button
                  onClick={() => setShowAuthDropdown(!showAuthDropdown)}
                  className="flex items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-[#1a2536] transition-colors"
                >
                  <User className="w-5 h-5 mr-1" />
                  <ChevronDown className="w-4 h-4 text-orange-500" />
                </button>
              )}

              {showAuthDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 ring-1 ring-black ring-opacity-5">
                  {isAuthenticated ? (
                    <>
                      <Link
                        href="/dashboard"
                        className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                        onClick={handleLinkClick}
                      >
                        Dashboard
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        href="/signin"
                        className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                        onClick={handleLinkClick}
                      >
                        Sign In
                      </Link>
                      <Link
                        href="/signup"
                        className="block px-4 py-2 text-sm text-orange-500 font-medium hover:bg-gray-100"
                        onClick={handleLinkClick}
                      >
                        Sign Up
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>

          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMenu && (
        <div id="mobile-menu" className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 border-t border-white/10">
            <Link
              href="/"
              className="block px-3 py-2 rounded-md text-base font-medium hover:bg-[#1a2536] transition-colors"
              onClick={handleLinkClick}
            >
              Home
            </Link>
            <Link
              href="/pricing"
              className="block px-3 py-2 rounded-md text-base font-medium hover:bg-[#1a2536] transition-colors"
              onClick={handleLinkClick}
            >
              Pricing
            </Link>
            <Link
              href="/products"
              className="block px-3 py-2 rounded-md text-base font-medium hover:bg-[#1a2536] transition-colors"
              onClick={handleLinkClick}
            >
              Products
            </Link>
            <Link
              href="/apply"
              className="block px-3 py-2 rounded-md text-base font-medium hover:bg-[#1a2536] transition-colors"
              onClick={handleLinkClick}
            >
              Apply
            </Link>
            <Link
              href="/get-started"
              className="block px-3 py-2 rounded-md text-base font-medium hover:bg-[#1a2536] transition-colors"
              onClick={handleLinkClick}
            >
              Get Started
            </Link>
            {isAuthenticated && (
              <Link
                href="/dashboard"
                className="block px-3 py-2 rounded-md text-base font-medium hover:bg-[#1a2536] transition-colors"
                onClick={handleLinkClick}
              >
                Dashboard
              </Link>
            )}

            {/* Mobile Support Options */}
            <div className="pt-4 pb-3 border-t border-white/10">
              <div className="px-3">
                <p className="text-sm font-medium text-white/80">Support</p>
              </div>
              <div className="mt-3 space-y-1">
                <a
                  href="tel:3053067774"
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium hover:bg-[#1a2536] transition-colors"
                  onClick={handleLinkClick}
                >
                  <Phone className="w-5 h-5 mr-3 text-orange-500" />
                  <span>(305) 306-7774</span>
                </a>
                <button
                  onClick={handleChatClick}
                  className="flex items-center w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-[#1a2536] transition-colors"
                >
                  <MessageSquare className="w-5 h-5 mr-3 text-orange-500" />
                  <span>Chat with us</span>
                </button>
                <a
                  href="mailto:support@midapos.com"
                  className="flex items-center px-3 py-2 rounded-md text-base font-medium hover:bg-[#1a2536] transition-colors"
                  onClick={handleLinkClick}
                >
                  <MessageSquare className="w-5 h-5 mr-3 text-orange-500" />
                  <span>support@midapos.com</span>
                </a>
              </div>
            </div>

            {/* Mobile Auth Options */}
            <div className="pt-4 pb-3 border-t border-white/10">
              {isAuthenticated ? (
                <div className="space-y-1">

                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-400 hover:bg-[#1a2536] transition-colors"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="space-y-3 px-3">
                  <Link
                    href="/signin"
                    className="block w-full px-4 py-2 rounded-md bg-[#1a2536] text-center text-white font-medium hover:bg-[#263549] transition-colors"
                    onClick={handleLinkClick}
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/signup"
                    className="block w-full px-4 py-2 rounded-md bg-orange-700 text-center text-white font-medium hover:bg-orange-800 transition-colors"
                    onClick={handleLinkClick}
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}